import * as React from 'react';
import { findIndex, propEq } from 'ramda';
import {
  ButtonGroup,
  IconButton,
  Slider,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
} from '@mui/material';
import {
  SkipPrevious,
  SkipNext,
  VolumeUpOutlined,
  VolumeOffOutlined,
  PlayCircle,
} from '@mui/icons-material';
import { convertSecondsToMilliseconds, isNil, RegionAPI, timelineLabel } from '@shared';
import { ControlsPropsType } from './Video.types';
import * as S from './Video.styled';

const PLAYBACK_RATES = [
  { name: '1x', value: 1 },
  { name: '2x', value: 2 },
  { name: '4x', value: 4 },
];

export const Controls = (props: ControlsPropsType) => {
  const {
    isDisabled,
    playing,
    setPlaying,
    playbackRate,
    onSetPlaybackRate,
    playedSeconds,
    onSeekChange,
    regions,
    videoDuration,
    muted,
    onMuted = () => null,
    volume,
    handleChangeVolume,
    buffering,
  } = props;

  const handleSeekChange = (index: number) => {
    const region = regions[index];
    const start = region.start;
    onSeekChange(start / videoDuration);
  };

  const getIndexCurrentAnomaly = () => {
    const findCurrentRegionIndex = (regions: Array<RegionAPI>, time: number) => {
      const closest = regions.reduce((prev, curr) => {
        return Math.abs(curr.start - time) < Math.abs(prev.start - time) ? curr : prev;
      });

      return findIndex(propEq('id', closest.id))(regions);
    };
    return findCurrentRegionIndex(regions, convertSecondsToMilliseconds(playedSeconds));
  };
  const handleSetPreviousAnomaly = () => {
    const currentRegionIndex = getIndexCurrentAnomaly();
    const lastRegionIndex = regions.length - 1;
    let regionIndex = currentRegionIndex - 1;

    if (isNil(currentRegionIndex) || currentRegionIndex === 0) {
      regionIndex = lastRegionIndex;
    }
    handleSeekChange(regionIndex);
    setPlaying(true);
  };
  const handleSetNextAnomaly = () => {
    const currentRegionIndex = getIndexCurrentAnomaly();
    const lastRegionIndex = regions.length - 1;
    let regionIndex = currentRegionIndex + 1;

    if (isNil(currentRegionIndex) || currentRegionIndex === lastRegionIndex) {
      regionIndex = 0;
    }

    handleSeekChange(regionIndex);
    setPlaying(true);
  };

  const handleSetPlaybackRate = (e: React.MouseEvent<HTMLElement>, value: number) => {
    onSetPlaybackRate(value);
  };

  const handlePlayPause = () => {
    setPlaying(!playing);
  };

  const handleSetAudioVolume = (e: Event, value: number | number[]) => {
    const newVolume = Array.isArray(value) ? value[0] : value;
    const isMuted = newVolume === 0;
    onMuted(isMuted);
    handleChangeVolume(newVolume);
  };

  const onMute = () => {
    const value = muted ? 1 : 0;
    handleChangeVolume(value);
    onMuted(!muted);
  };

  return (
    <>
      <S.PlayButton disableRipple onClick={handlePlayPause} disabled={isDisabled}>
        {buffering ? (
          <CircularProgress size={50} sx={{ color: '#ffffff80' }} />
        ) : (
          !playing && <PlayCircle sx={{ fontSize: 80 }} />
        )}
      </S.PlayButton>
      <S.Controls>
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <ToggleButtonGroup
            sx={{ backgroundColor: 'rgba(255,255,255,0.7)' }}
            value={isDisabled ? undefined : playbackRate}
            exclusive
            onChange={handleSetPlaybackRate}
          >
            {PLAYBACK_RATES.map(({ name, value }) => {
              return (
                <ToggleButton
                  key={value}
                  name='rate'
                  value={value}
                  color='primary'
                  disabled={isDisabled}
                >
                  {name}
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>
          {regions && regions.length > 0 && (
            <ButtonGroup sx={{ backgroundColor: 'rgba(255,255,255,0.7)' }}>
              <S.ToAnomalyButton onClick={handleSetPreviousAnomaly} disabled={isDisabled}>
                <SkipPrevious htmlColor='rgba(0, 0, 0, 0.54)' />
              </S.ToAnomalyButton>
              <S.ToAnomalyButton onClick={handleSetNextAnomaly} disabled={isDisabled}>
                <SkipNext htmlColor='rgba(0, 0, 0, 0.54)' />
              </S.ToAnomalyButton>
            </ButtonGroup>
          )}
          <Stack direction='row' alignItems='center' gap={1}>
            <Slider
              sx={{ minWidth: '70px', color: 'white' }}
              value={volume}
              onChange={handleSetAudioVolume}
              disabled={isDisabled}
              min={0}
              max={1}
              step={0.01}
            />
            <IconButton onClick={onMute} disabled={isDisabled}>
              {muted || volume === 0 ? (
                <VolumeOffOutlined htmlColor='white' />
              ) : (
                <VolumeUpOutlined htmlColor='white' />
              )}
            </IconButton>
          </Stack>

          {!isNil(playedSeconds) && <S.Stopwatch>{timelineLabel(playedSeconds)}</S.Stopwatch>}
        </Stack>
      </S.Controls>
    </>
  );
};
